import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getCustomerDetails,
  getCustomerExtendPayments,
  getCustomerPayments,
} from "redux/features/customer/actions";
import { setPagerStatePayments as setPagerState } from "redux/features/customer";
import { toast } from "react-toastify";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { useCustomLocalization } from "utils";
import CellOperationNumber from "./cellOperationNumber";
import CellCode from "./cellCode";
import CellDate from "./cellDate";
import CellDownload from "./cellDownload";
import CellTotal from "./cellTotal";
import CellPaymentType from "./cellPaymentType";
import EmptySearch from "components/emptySearch";
import HeaderCustomerPayments from "./header";
import Loader from "components/loader";
import "./tab-payments.scss";
import defaultMessages from "i18n/en.json";
import CellDocumentType from "./cellDocumentType";
import CellGenericString from "./cellGenericString";

const useLocalizedMessages = () => {
  const [toLanguageString] = useCustomLocalization();
  return {
    labelEmptyListCustomerPayments: toLanguageString(
        "metislab.frontend.components.customer.components.details.tabs.tabPayments.emptyListCustomerPayments.label",
        defaultMessages.metislab.frontend.components.customer.components.details
            .tabs.tabPayments.emptyListCustomerPayments.label
    ),
    labelStrongEmptyListCustomerPayments: toLanguageString(
        "metislab.frontend.components.customer.components.details.tabs.tabPayments.emptyListCustomerPayments.labelStrong",
        defaultMessages.metislab.frontend.components.customer.components.details
            .tabs.tabPayments.emptyListCustomerPayments.labelStrong
    ),
    labelEmptySearch: toLanguageString(
        "metislab.frontend.components.customer.components.details.tabs.tabPayments.emptySearch.label",
        defaultMessages.metislab.frontend.components.customer.components.details
            .tabs.tabPayments.emptySearch.label
    ),
    labelStrongEmptySearch: toLanguageString(
        "metislab.frontend.components.customer.components.details.tabs.tabPayments.emptySearch.labelStrong",
        defaultMessages.metislab.frontend.components.customer.components.details
            .tabs.tabPayments.emptySearch.labelStrong
    ),
    totalAmount: toLanguageString(
        "metislab.frontend.components.customer.components.details.tabs.tabPayments.totalAmount",
        defaultMessages.metislab.frontend.components.customer.components.details
            .tabs.tabPayments.totalAmount
    ),
  };
};

const TabPayments = ({ id, isExtended }) => {
  const {
    labelEmptyListCustomerPayments,
    labelStrongEmptyListCustomerPayments,
    labelEmptySearch,
    labelStrongEmptySearch,
    totalAmount,
  } = useLocalizedMessages();

  const dispatch = useDispatch();

  const currencyStdWidth = 150;
  /* const intl = useInternationalization(); */

  const authenticated = useSelector((state) => state.user.authenticated);
  const customerId = useSelector((state) => state.user.customerId);

  const layoutColumn = useSelector(
      (state) => state.customer.layoutColumnPayments
  );
  const columnsDef = useSelector(
      (state) => state.customer.columnsDef
  );
  const loading = useSelector((state) => state.customer.loadingPayments);
  const pagerState = useSelector((state) => state.customer.pagerStatePayments);
  const periodState = useSelector((state) => state.customer.periodState);
  const pagination = useSelector((state) => state.customer.paginationPayments);
  const pageSizes = useSelector((state) => state.customer.pageSizesPayments);
  const payments = useSelector((state) => state.customer.payments);
  const paymentsAmountTotal = useSelector(
      (state) => state.customer.paymentsAmountTotal
  );

  /**
   * Get the list of payments
   */
  useEffect(() => {
    if (authenticated) {
      const args = {
        id: id === undefined ? customerId : id,
        pager: isExtended ? {} : pagerState,
        period: isExtended ? periodState : {},
        toast,
      };
      if (isExtended) {
        dispatch(getCustomerExtendPayments(args));
      } else {
        dispatch(getCustomerPayments(args));
      }
    }
  }, [authenticated, customerId, dispatch, id, pagerState,periodState]);

  /**
   * pageChange is the function to handle the change between pages
   * @param {object} e - It's an event object
   */
  const pageChange = (e) => {
    const { skip, take } = e.page;
    dispatch(
        setPagerState({
          page: skip / take + 1,
          perPage: take,
        })
    );
  };

  /**
   * sortChange is the function to handle the sort of customers by column
   * @param {object} e - It's an event object
   */
  const sortChange = (e) => {
    if (e.sort.length) {
      const { field, dir } = e.sort[0];
      dispatch(
          setPagerState({
            orderBy: field,
            orderByDirection: dir,
          })
      );
    } else {
      dispatch(
          setPagerState({
            orderBy: null,
            orderByDirection: null,
          })
      );
    }
  };

  /**
   * Render the cell in column operation's number
   * @param {object} data
   * @returns {CellOperationNumber} component
   */
  const cellOperationNumber = (data) => {
    const { operation_expired, operation_number } = data.dataItem;

    return (
        <CellOperationNumber
            isExpired={operation_expired}
            operationNumber={operation_number}
        />
    );
  };

  /**
   * Render the cell in column Code
   * @param {object} data
   * @returns {CellCode} component
   */
  const cellCode = (data) => {
    const states = layoutColumn.find(
        (column) => column.propId === "payment_code"
    ).editor.data.rows;
    const stateLabel = states.find(
        (state) => state.value === data?.dataItem?.payment_code
    );

    return (
        <CellCode
            code={stateLabel?.label}
            isExpired={data?.dataItem?.operation_expired}
        />
    );
  };

  /**
   * Render the cell in column data creation
   * @param {object} data
   * @returns {CellDate} component
   */
  const CellDateCreation = (data) => {
    const { document_date, operation_expired } = data.dataItem;
    return <CellDate date={document_date} isExpired={operation_expired} convertTimezone={false} />;
  };

  /**
   * Render the cell in column data expiration
   * @param {object} data
   * @returns {CellDate} component
   */
  const cellDateExpiration = (data) => {
    const { expiration_date, operation_expired } = data.dataItem;
    return <CellDate date={expiration_date} isExpired={operation_expired} convertTimezone={false} />;
  };

  /**
   * Render the cell in column data expiration
   * @param {object} data
   * @returns {CellDate} component
   */
  const cellOperationDate = (data) => {
    const { operation_date, operation_expired } = data.dataItem;
    return <CellDate date={operation_date} isExpired={operation_expired} convertTimezone={false} />;
  };

  /**
   * Render the cell in column download
   * @param {object} data
   * @returns {CellDownload} component
   */
  const cellDownload = (data) => {
    const { document_id } = data.dataItem;

    return <CellDownload idDocs={document_id} />;
  };

  /**
   * Render the cell in column data amount
   * @param {object} data
   * @returns {CellTotal} component
   */
  const cellTotalResidual = (data) => {
    const { residual_amount, operation_expired } = data.dataItem;

    return <CellTotal data={residual_amount} isExpired={operation_expired} />;
  };

  /**
   * Render the cell in column data amount
   * @param {object} data
   * @returns {CellTotal} component
   */
  const cellTotalAmount = (data) => {
    const { total_amount, operation_expired } = data.dataItem;

    return <CellTotal data={total_amount} isExpired={operation_expired} />;
  };

  /**
   * Render the cell in column data amount
   * @param {object} data
   * @returns {CellTotal} component
   */
  const cellDocumentType = (data) => {
    const { document_type, operation_expired } = data.dataItem;

    return <CellDocumentType data={document_type} isExpired={operation_expired} />;
  };

  /**
   * Render the cell in column generic string
   * @param {object} data
   * @returns {CellTotal} component
   */
  const CellGenericStringMovement = (data) => {
    const { movement_type_description, operation_expired } = data.dataItem;

    return <CellGenericString data={movement_type_description} isExpired={operation_expired} />;
  };
  /**
   * Render the cell in column generic string
   * @param {object} data
   * @returns {CellTotal} component
   */
  const CellGenericStringReason = (data) => {
    const { reason_code, operation_expired } = data.dataItem;
    let reason_label  = "";
    if(columnsDef.find((el)=>el.propId === 'reason_code') &&
        columnsDef.find((el)=>el.propId === 'reason_code').editor.data.rows.find(_el=>_el.value === reason_code)) {
      reason_label = columnsDef.find((el) => el.propId === 'reason_code') && columnsDef.find((el) => el.propId === 'reason_code')?.editor.data.rows.find(_el => _el.value === reason_code)?.label;
    }
    return <CellGenericString data={reason_label} isExpired={operation_expired} />;
  };
  /**
   * Render the cell in column generic string
   * @param {object} data
   * @returns {CellTotal} component
   */
  const CellGenericStringOpState = (data) => {
    const { operation_state, operation_expired } = data.dataItem;

    const stateLabel = columnsDef
        .find((col) => col.propId === "operation_state")?.editor?.data?.rows
        .find((row) => row.value === operation_state)?.label || operation_state;

    return <CellGenericString data={stateLabel} isExpired={operation_expired} />;
  };

  /**
   * Render the cell in column data amount
   * @param {object} data
   * @returns {CellTotal} component
   */
  const cellPaymentType = (data) => {
    const { payment_type, operation_expired } = data.dataItem;

    return (
        <CellPaymentType data={payment_type} isExpired={operation_expired} />
    );
  };

  //Situazione finanziaria estesa

  /**
   * Render the cell in column progressive_total
   * @param {object} data
   * @returns {CellTotal} component
   */
  const cellProgressiveTotal = (data) => {
    const { progressive_total, operation_expired } = data.dataItem;

    return <CellTotal data={progressive_total} isExpired={operation_expired} />;
  };

  /**
   * Render the cell in column residual_amount
   * @param {object} data
   * @returns {CellTotal} component
   */
  const cellResidualAmount = (data) => {
    const { residual_amount, operation_expired } = data.dataItem;

    return <CellTotal data={residual_amount} isExpired={operation_expired} />;
  };

  /**
   * Render the cell in column operation
   * @param {object} data
   * @returns {CellCode} component
   */
  const cellOperation = (data) => {
    return <CellCode code={data?.dataItem?.operation} />;
  };

  /**
   * Render the cell in column operation_reason
   * @param {object} data
   * @returns {CellCode} component
   */
  const cellOperationReason = (data) => {
    return <CellCode code={data?.dataItem?.operation_reason} />;
  };

  /**
   * Render the cell in column operation
   * @param {object} data
   * @returns {CellCode} component
   */
  const cellExposure = (data) => {
    return <CellCode code={data?.dataItem?.exposure} />;
  };


  /**
   * cella epr la testata
   */
  const CustomHeaderCell = (props) => {
    const { width } = props;
    const isCurrency = ['progressive_total','total_amount','residual_amount'].includes(props.field)

    // const width = layoutColumn.find(
    //     (column) => column.propId === props.field
    // ).editor.data.rows;
    return (
        <span className="k-cell-inner"
              style={{
                width: `${width}px`, // Larghezza ricevuta
                display: "block",
              }}>
          <span className="k-link" style={isCurrency ? {textAlign: "right", display: "block"} : {}}>
            <span className={isCurrency ?  "k-column-title-alt" : "k-column-title"} >
              {props.title}
            </span>
          </span>
        </span>)

  };


  return (
      <>
        <HeaderCustomerPayments id={id} isExtended={isExtended}/>

        {loading ? (
            <Loader className="c-loader--center c-loader--absolute" size="medium"/>
        ) : payments && payments?.length === 0 ? (
            <EmptySearch
                icon={pagerState.search ? "search" : null}
                strongText={
                  pagerState.search
                      ? labelStrongEmptySearch
                      : labelStrongEmptyListCustomerPayments
                }
                text={
                  pagerState.search
                      ? labelEmptySearch
                      : labelEmptyListCustomerPayments
                }
            />
        ) : (
            <div>
              <section className="table-container">
                <Grid
                    className={isExtended ? 'tabella-estesa' : ""}
                    data={payments}
                    pageable={
                      isExtended
                          ? false
                          : {
                            buttonCount: 5,
                            info: false,
                            pageSizes,
                          }
                    }
                    onPageChange={isExtended ? undefined : pageChange}
                    skip={
                      isExtended
                          ? undefined
                          : (pagerState?.page - 1) * pagerState?.perPage
                    }
                    sortable={!isExtended ? {
                      allowUnsort: true,
                      mode: "single",
                    }: undefined}
                    sort={[
                      {
                        field: pagerState.orderBy,
                        dir: pagerState.orderByDirection,
                      },
                    ]}
                    onSortChange={sortChange}
                    take={isExtended ? undefined : pagination?.size}
                    total={isExtended ? undefined : pagination?.total}
                >
                  {layoutColumn &&
                      layoutColumn.map((column) => {
                        if (!column.hidden || column.propId === "id") {
                          let cell = null;

                          let _width = 'auto';
                          switch (column?.propId) {
                            case "progressive_total":
                              if(isExtended) cell = cellProgressiveTotal;
                              _width = currencyStdWidth;
                              break;
                            case "operation":
                              if(isExtended) cell = cellOperation;
                              _width = currencyStdWidth;
                              break;
                            case "operation_reason":
                              if(isExtended) cell = cellOperationReason;
                              _width = currencyStdWidth;
                              break;
                              // 26 02 SAL
                              // case "exposure":
                              //   if(isExtended) cell = cellExposure;
                              //   break;
                            case "operation_number":
                              cell = cellOperationNumber;
                              _width = currencyStdWidth;
                              break;
                            case "payment_code":
                              cell = cellCode;
                              _width = 180;
                              break;
                            case "operation_date":
                              cell = cellOperationDate;
                              _width = currencyStdWidth;
                              break;
                            case "document_date":
                              cell = CellDateCreation;
                              _width = currencyStdWidth;
                              break;
                            case "expiration_date":
                              cell = cellDateExpiration;
                              _width = currencyStdWidth;
                              break;
                            case "id":
                              cell = cellDownload;
                              _width = currencyStdWidth;
                              break;
                            case "document_type":
                              if(isExtended) cell = cellDocumentType;
                              _width = currencyStdWidth;
                              break;
                              // 26 02 SAL
                              // case "movement_type_description":
                              //   if(isExtended) cell = CellGenericStringMovement;
                              //   break;
                            case "reason_code":
                              if(isExtended) cell = CellGenericStringReason;
                              _width = 180;
                              break;

                            case "operation_state":
                              if(isExtended) cell = CellGenericStringOpState;
                              _width = currencyStdWidth;
                              break;

                            case "residual_amount":
                              cell = cellResidualAmount;
                              _width = currencyStdWidth;
                              break;

                            case "total_amount":
                              cell = cellTotalAmount;
                              _width = currencyStdWidth;
                              break;
                              //17/03/25
                           // case "payment_type":
                           //   cell = cellPaymentType;
                           //   _width = currencyStdWidth;
                            //  break;
                            default:
                              cell = null;
                              break;
                          }
                         // console.log("Colonna:", column.propId, "Hidden:", column.hidden);

                          /**
                          * controllo che la width non sia quella standard di NOVA che è 50, se è diversa vuol dire che il back ha specificato una width
                          * negli altri casi prende la width auto o specificata nei vari campi.
                          */
                          if(column.width && column.width !== 50) _width = column.width;

                          return cell === null ? undefined : (
                              <Column
                                  key={column?.propId}
                                  field={column?.propId}
                                  width={_width}
                                  title={column?.propId === "id" ? " " : column?.label}
                                  cell={cell}
                                  headerCell={(props) => <CustomHeaderCell {...props} width={_width}/> } //La stessa larghezza anche alle intestazioni
                                  sortable={column?.propId === "id" ? false : true}
                              />
                          );
                        }
                        return null;
                      })}
                </Grid>
              </section>
              {paymentsAmountTotal && !isExtended && <div className="c-total">
                {/* {totalAmount} <strong>{intl.formatNumber(paymentsAmountTotal, "c")}</strong> */}
                {totalAmount} <strong>{paymentsAmountTotal}</strong>
              </div>}
            </div>
        )}
      </>
  );
};

export default TabPayments;
